import type { ReactNode } from 'react';

import { useControllableState } from '../../hooks';
import { addWithConfig } from '../../utils';
import { Box } from '../box';
import { Pressable } from '../pressable';
import { Text } from '../text';

import { type ToggleButtonGroupProps, type _ToggleButtonProps } from './types';
import { colors } from '@fhs/ui/src/tokens/colors';

const BORDER_WIDTH_PX = 1;

function getStringLabelForOption(option?: {
  accessibilityLabel?: string;
  label: ReactNode;
  value: unknown;
}): string {
  if (!option) {
    return '';
  }

  if (option.accessibilityLabel) {
    return option.accessibilityLabel;
  }

  if (typeof option.label === 'string') {
    return option.label;
  }

  return '';
}

// eslint-disable-next-line complexity
function ToggleButton({
  label,
  accessibilityLabel,
  isSelected,
  isDisabled,
  isInvalid,
  growToFit,
  boldButtonText = false,
  ...props
}: _ToggleButtonProps) {
  return (
    <Pressable
      accessibilityRole="radio"
      accessibilityLabel={accessibilityLabel}
      accessibilityHint=""
      accessibilityState={{ disabled: isDisabled, selected: isSelected }}
      backgroundColor="token.background-input"
      minHeight="$12"
      alignContent="center"
      justifyContent="center"
      flexGrow={growToFit ? '1' : '0'}
      flexShrink={1}
      borderWidth={`${BORDER_WIDTH_PX}px`}
      borderColor="token.border-color-input"
      _hover={{
        borderColor: colors.$houseRed,
        // based on v2 scheduled pick up pills
        color: 'token.background-input',
        zIndex: 1,
      }}
      _pressed={{
        borderColor: colors.$houseRedOpacity10,
        backgroundColor: colors.$houseRedOpacity04,
        zIndex: 1,
      }}
      {...(isInvalid && {
        borderColor: 'token.border-color-error',
        _hover: {
          backgroundColor: colors.$houseRedOpacity04,
        },
      })}
      {...(isSelected && {
        backgroundColor: colors.$houseRedOpacity20,
        borderColor: 'transparent',
        zIndex: 1,
        _hover: {},
        _pressed: {},
      })}
      {...(isDisabled && {
        disabled: true,
        color: 'blackOpacity.5',
        backgroundColor: 'token.background-input-disabled',
        borderColor: 'token.border-color-disabled',
        zIndex: 1,
        _hover: {},
        _pressed: {},
      })}
      {...props}
    >
      <Text
        textAlign="center"
        fontWeight={boldButtonText ? 'bold' : undefined}
        color="token.text-default"
        {...(isDisabled && {
          color: 'token.text-disabled',
        })}
        whiteSpace="nowrap"
      >
        {label}
      </Text>
    </Pressable>
  );
}

/* eslint-disable-next-line complexity */
export function ToggleButtonGroup<ValueType extends string>({
  value,
  onChangeValue,
  options,
  isDisabled,
  isInvalid,
  accessibilityLabel,
  accessibilityHint,
  growToFit = true,
  boldButtonText = false,
  _toggleButton,
  ...boxProps
}: ToggleButtonGroupProps<ValueType>) {
  const [selectedOptionValue, setSelectedOptionValue] = useControllableState({
    defaultValue: null,
    value,
    handleValueChange: onChangeValue,
  });

  const selectedOption = selectedOptionValue
    ? options.find(opt => opt.value === selectedOptionValue)
    : undefined;

  return (
    <Box
      accessibilityLabel={accessibilityLabel}
      accessibilityHint={accessibilityHint}
      accessibilityRole="radiogroup"
      display="flex"
      flexDirection="row"
      flexWrap="nowrap"
      accessibilityValue={{
        text: getStringLabelForOption(selectedOption) ?? undefined,
      }}
      maxWidth="100%"
      {...boxProps}
    >
      {options.map(option => (
        <ToggleButton
          key={option.value}
          isSelected={selectedOptionValue === option.value}
          onPress={() => setSelectedOptionValue(option.value)}
          isDisabled={isDisabled || option.isDisabled}
          isInvalid={isInvalid}
          label={option.label}
          accessibilityLabel={getStringLabelForOption(option)}
          accessibilityHint=""
          growToFit={growToFit}
          boldButtonText={boldButtonText}
          {..._toggleButton}
        />
      ))}
    </Box>
  );
}

export default addWithConfig(ToggleButtonGroup);
